import {nanoid} from 'nanoid'
import {
    useShopperCustomersMutation,
    useShopperBasketsMutation,
} from '@salesforce/commerce-sdk-react'

const stripEmojis = (str) =>
    str.replace(
        new RegExp(
            '\\p{Extended_Pictographic}|\\p{Emoji_Component}|\\p{Emoji_Presentation}|\\p{Cf}',
            'gu',
        ),
        '',
    )

/**
 * Custom hook that provides actions related to the checkout process.
 *
 * @returns {Object} An object containing the following functions:
 * - submitAddresses: Submits the provided address information for the customer and basket.
 * - updateBasketWithGift: Updates the basket with gift values.
 *
 * @function
 * @name useCheckoutActions
 *
 * @example
 * const { submitAddresses, updateBasketWithGift } = useCheckoutActions();
 *
 * @typedef {Object} Address
 * @property {string} addressId - The unique identifier for the address.
 * @property {string} address1 - The first line of the address.
 * @property {string} address2 - The second line of the address (optional).
 * @property {string} city - The city of the address.
 * @property {string} countryCode - The country code of the address.
 * @property {string} firstName - The first name of the person associated with the address.
 * @property {string} lastName - The last name of the person associated with the address.
 * @property {string} phone - The phone number associated with the address.
 * @property {string} postalCode - The postal code of the address.
 * @property {string} stateCode - The state code of the address.
 * @property {string} [vatNumber] - The VAT number for the address (optional).
 * @property {string} [companyName] - The company name for the address (optional).
 * @property {string} [sdi] - The SDI code for the address (optional).
 * @property {boolean} [vatConfirmed] - Whether the VAT number is confirmed (optional).
 * @property {string} [fiscalCode] - The fiscal code for the address (optional).
 *
 * @typedef {Object} Customer
 * @property {boolean} isRegistered - Indicates if the customer is registered.
 * @property {string} customerId - The unique identifier for the customer.
 *
 * @typedef {Object} Basket
 * @property {string} basketId - The unique identifier for the basket.
 *
 * @param {Customer} customer - The customer object.
 * @param {Basket} basket - The basket object.
 * @param {Address} address - The address object.
 * @param {boolean} [isBillingAddress=false] - Indicates if the address is a billing address.
 *
 * @async
 * @function submitAddresses
 * @description Submits the provided address information for the customer and basket.
 *
 * @param {Basket} basket - The basket object.
 * @param {Object} giftValues - The gift values to update in the basket.
 *
 * @function updateBasketWithGift
 * @description Updates the basket with gift values.
 */
export const useCheckoutActions = () => {
    const createCustomerAddress = useShopperCustomersMutation('createCustomerAddress')
    const updateCustomerAddress = useShopperCustomersMutation('updateCustomerAddress')
    const updateShippingAddressForShipment = useShopperBasketsMutation(
        'updateShippingAddressForShipment',
    )
    const updateShipmentForBasket = useShopperBasketsMutation('updateShipmentForBasket')
    const updateBasket = useShopperBasketsMutation('updateBasket')
    const createBody = (address, isBillingAddress, isDefaultCheckbox) => {
        const {
            addressId,
            address1,
            address2,
            city,
            countryCode,
            firstName,
            lastName,
            phone,
            postalCode,
            stateCode,
            title,
            c_addressId,
            c_isPreferredAddress,
            c_regionName,
        } = address

        let _body = {
            address1,
            address2,
            city,
            countryCode: typeof countryCode === 'object' ? countryCode?.value : countryCode,
            firstName,
            lastName,
            phone,
            postalCode,
            stateCode: typeof stateCode === 'object' ? stateCode?.value : stateCode,
            title: typeof title === 'object' ? title?.value : title,
            c_countryName: typeof countryCode === 'object' ? countryCode?.label : countryCode,
            c_regionName:
                typeof stateCode === 'object' ? stateCode?.label : c_regionName ?? stateCode,
            c_isPreferredAddress: isDefaultCheckbox ? true : c_isPreferredAddress,
        }

        if (isBillingAddress) {
            if (address.c_vatNumber) {
                const {c_vatNumber, c_companyName, c_sdi, c_vatNumberVerified} = address
                _body = {
                    ..._body,
                    c_vatNumber,
                    c_companyName,
                    c_sdi,
                    c_vatNumberVerified,
                    c_isB2BAddress: true,
                }
            } else if (address.firstName && address.lastName) {
                // Remove if leftover
                delete address.c_vatNumberVerified

                _body = {
                    ..._body,
                    c_isB2CBillingAddress: true,
                }

                if (address.c_fiscalCode) {
                    _body['c_fiscalCode'] = address.c_fiscalCode
                }
            }
        }

        return {addressId, c_addressId, body: _body}
    }

    const submitAddresses = async (customer, basket, address, isBillingAddress = false) => {
        const {addressId, c_addressId, body: _body} = createBody(address, isBillingAddress)
        if (!isBillingAddress)
            await updateShippingAddressForShipment.mutateAsync({
                parameters: {
                    basketId: basket.basketId,
                    shipmentId: 'me',
                    useAsBilling: false,
                },
                body: {..._body, c_addressId: addressId},
            })

        if (customer.isRegistered && !addressId && !c_addressId) {
            const body = {
                ..._body,
                addressId: nanoid(),
            }
            const res = await createCustomerAddress.mutateAsync({
                body,
                parameters: {customerId: customer.customerId},
            })
            return res
        }

        if (customer.isRegistered && addressId) {
            await updateCustomerAddress.mutateAsync({
                body: {
                    ..._body,
                    addressId,
                },
                parameters: {
                    customerId: customer.customerId,
                    addressName: addressId,
                },
            })
        }
    }

    const updateBasketWithGift = async (basket, giftValues) => {
        const body = {
            gift: giftValues.gift || false,
        }
        if (giftValues.giftMessage) {
            body.giftMessage = stripEmojis(giftValues.giftMessage)
        }
        updateShipmentForBasket.mutate({
            parameters: {shipmentId: 'me', basketId: basket.basketId},
            body,
        })
    }

    const updateBasketWithGigyaGuestId = async (basket, guest_gigyaId) => {
        const body = {
            c_gigyaGuestId: guest_gigyaId ? guest_gigyaId : 'NO_GIGYA_ID',
        }
        await updateBasket.mutateAsync({
            parameters: {basketId: basket.basketId},
            body,
        })
    }

    const updateBasketWithVatVerified = async (basket, vatVerified) => {
        const body = {
            c_isVatApproved: vatVerified ? vatVerified : null,
        }
        await updateBasket.mutateAsync({
            parameters: {basketId: basket.basketId},
            body,
        })
    }

    return {
        submitAddresses,
        updateBasketWithGift,
        createBody,
        updateBasketWithGigyaGuestId,
        updateBasketWithVatVerified,
    }
}
